import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, Save, X } from 'lucide-react';
import './style/EditStudent.css';

const EditStudent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('basic');

  useEffect(() => {
    const fetchStudentDetails = () => {
      setTimeout(() => {
        const mockStudent = {
          id: id,
          name: 'John Doe',
          photo: 'https://www.hire4event.com/apppanel/assets/artistimage/artistphoto/166962058401669620443242.jpg',
          admissionNumber: 'A001',
          class: 'XI-A',
          currentGrade: 'A',
          attendance: '95%',
          previousTestMarks: [
            { subject: 'Math', mark: 95 },
            { subject: 'Science', mark: 88 },
            { subject: 'English', mark: 92 },
            { subject: 'History', mark: 85 },
            { subject: 'Geography', mark: 90 },
          ],
          otherDetails: {
            dateOfBirth: '2005-05-15',
            parentName: 'Jane Doe',
            contactNumber: '+1234567890',
            address: '123 School Street, City, Country',
          },
          achievements: [
            'First place in Science Fair 2022',
            'Captain of the school debate team',
            'Perfect attendance award for 2 consecutive years',
          ],
          extracurricular: [
            'Member of the school choir',
            'Volunteer at local animal shelter',
            'Participant in Model United Nations',
          ],
          skills: [
            { name: 'Leadership', level: 90 },
            { name: 'Speaking', level: 85 },
            { name: 'Problem Solving', level: 92 },
            { name: 'Teamwork', level: 88 },
          ],
        };
        setStudent(mockStudent);
        setIsLoading(false);
      }, 1000);
    };

    fetchStudentDetails();
  }, [id]);

  const handleInputChange = (e, section = null, index = null) => {
    const { name, value } = e.target;
    if (section) {
      if (index !== null) {
        setStudent(prevStudent => ({
          ...prevStudent,
          [section]: prevStudent[section].map((item, i) => 
            i === index ? { ...item, [name]: value } : item
          )
        }));
      } else {
        setStudent(prevStudent => ({
          ...prevStudent,
          [section]: { ...prevStudent[section], [name]: value }
        }));
      }
    } else {
      setStudent(prevStudent => ({ ...prevStudent, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Updated student data:', student);
    navigate(`/student/${id}`);
  };

  if (isLoading) {
    return (
      <div className="es-loading-container">
        <div className="es-loading-spinner"></div>
        <p>Loading student details...</p>
      </div>
    );
  }

  const renderSection = (sectionName) => {
    switch (sectionName) {
      case 'basic':
        return (
          <>
            <div className="es-form-group">
              <label htmlFor="name" className="es-label">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={student.name}
                onChange={handleInputChange}
                required
                className="es-input"
              />
            </div>
            <div className="es-form-group">
              <label htmlFor="admissionNumber" className="es-label">Admission Number</label>
              <input
                type="text"
                id="admissionNumber"
                name="admissionNumber"
                value={student.admissionNumber}
                onChange={handleInputChange}
                required
                className="es-input"
              />
            </div>
            <div className="es-form-group">
              <label htmlFor="class" className="es-label">Class</label>
              <input
                type="text"
                id="class"
                name="class"
                value={student.class}
                onChange={handleInputChange}
                required
                className="es-input"
              />
            </div>
            <div className="es-form-group">
              <label htmlFor="currentGrade" className="es-label">Current Grade</label>
              <input
                type="text"
                id="currentGrade"
                name="currentGrade"
                value={student.currentGrade}
                onChange={handleInputChange}
                required
                className="es-input"
              />
            </div>
            <div className="es-form-group">
              <label htmlFor="attendance" className="es-label">Attendance</label>
              <input
                type="text"
                id="attendance"
                name="attendance"
                value={student.attendance}
                onChange={handleInputChange}
                required
                className="es-input"
              />
            </div>
          </>
        );
      case 'personal':
        return (
          <>
            <div className="es-form-group">
              <label htmlFor="dateOfBirth" className="es-label">Date of Birth</label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={student.otherDetails.dateOfBirth}
                onChange={(e) => handleInputChange(e, 'otherDetails')}
                required
                className="es-input"
              />
            </div>
            <div className="es-form-group">
              <label htmlFor="parentName" className="es-label">Parent Name</label>
              <input
                type="text"
                id="parentName"
                name="parentName"
                value={student.otherDetails.parentName}
                onChange={(e) => handleInputChange(e, 'otherDetails')}
                required
                className="es-input"
              />
            </div>
            <div className="es-form-group">
              <label htmlFor="contactNumber" className="es-label">Contact Number</label>
              <input
                type="tel"
                id="contactNumber"
                name="contactNumber"
                value={student.otherDetails.contactNumber}
                onChange={(e) => handleInputChange(e, 'otherDetails')}
                required
                className="es-input"
              />
            </div>
            <div className="es-form-group es-full-width">
              <label htmlFor="address" className="es-label">Address</label>
              <textarea
                id="address"
                name="address"
                value={student.otherDetails.address}
                onChange={(e) => handleInputChange(e, 'otherDetails')}
                required
                className="es-textarea"
              ></textarea>
            </div>
          </>
        );
      case 'academic':
        return (
          <>
            {student.previousTestMarks.map((subject, index) => (
              <div key={index} className="es-form-group">
                <label htmlFor={`subject-${index}`} className="es-label">{subject.subject}</label>
                <input
                  type="number"
                  id={`subject-${index}`}
                  name="mark"
                  value={subject.mark}
                  onChange={(e) => handleInputChange(e, 'previousTestMarks', index)}
                  min="0"
                  max="100"
                  required
                  className="es-input"
                />
              </div>
            ))}
          </>
        );
      case 'achievements':
        return (
          <>
            {student.achievements.map((achievement, index) => (
              <div key={index} className="es-form-group">
                <label htmlFor={`achievement-${index}`} className="es-label">Achievement {index + 1}</label>
                <input
                  type="text"
                  id={`achievement-${index}`}
                  name={`achievement-${index}`}
                  value={achievement}
                  onChange={(e) => {
                    const newAchievements = [...student.achievements];
                    newAchievements[index] = e.target.value;
                    setStudent(prevStudent => ({ ...prevStudent, achievements: newAchievements }));
                  }}
                  className="es-input"
                />
              </div>
            ))}
          </>
        );
      case 'extracurricular':
        return (
          <>
            {student.extracurricular.map((activity, index) => (
              <div key={index} className="es-form-group">
                <label htmlFor={`activity-${index}`} className="es-label">Activity {index + 1}</label>
                <input
                  type="text"
                  id={`activity-${index}`}
                  name={`activity-${index}`}
                  value={activity}
                  onChange={(e) => {
                    const newActivities = [...student.extracurricular];
                    newActivities[index] = e.target.value;
                    setStudent(prevStudent => ({ ...prevStudent, extracurricular: newActivities }));
                  }}
                  className="es-input"
                />
              </div>
            ))}
          </>
        );
      case 'skills':
        return (
          <>
            {student.skills.map((skill, index) => (
              <div key={index} className="es-skill-edit">
                <div className="es-skill-name">
                  <label htmlFor={`skill-name-${index}`} className="es-label">Skill Name</label>
                  <input
                    type="text"
                    id={`skill-name-${index}`}
                    name="name"
                    value={skill.name}
                    onChange={(e) => handleInputChange(e, 'skills', index)}
                    className="es-input"
                  />
                </div>
                <div className="es-skill-level">
                  <label htmlFor={`skill-level-${index}`} className="es-label">Skill Level</label>
                  <input
                    type="range"
                    id={`skill-level-${index}`}
                    name="level"
                    value={skill.level}
                    onChange={(e) => handleInputChange(e, 'skills', index)}
                    min="0"
                    max="100"
                    className="es-input-range"
                  />
                  <span className="es-skill-level-value">{skill.level}%</span>
                </div>
              </div>
            ))}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="es-container">
      <div className="es-header">
        <img src={student.photo} alt={student.name} className="es-student-photo" />
        <h1 className="es-title">Edit Student Details</h1>
      </div>
      <form onSubmit={handleSubmit} className="es-form">
        <div className="es-tabs">
          {['basic', 'personal', 'academic', 'achievements', 'extracurricular', 'skills'].map((section) => (
            <button
              key={section}
              type="button"
              className={`es-tab ${activeSection === section ? 'es-active' : ''}`}
              onClick={() => setActiveSection(section)}
            >
              {section.charAt(0).toUpperCase() + section.slice(1)}
              <ChevronDown className="es-tab-icon" />
            </button>
          ))}
        </div>
        <div className="es-section">
          <h2 className="es-section-title">{activeSection.charAt(0).toUpperCase() + activeSection.slice(1)} Information</h2>
          <div className="es-grid">
            {renderSection(activeSection)}
          </div>
        </div>
        <div className="es-actions">
          <button type="submit" className="es-button es-save-button">
            <Save className="es-button-icon" />
            Save Changes
          </button>
          <button type="button" className="es-button es-cancel-button" onClick={() => navigate(`/student/${id}`)}>
            <X className="es-button-icon" />
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditStudent;